/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  get,
  getDatabase,
  onValue,
  push,
  ref,
  set,
  update,
} from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/contexts/AuthContext";
import { useCircleMembers } from "@/hooks/useCircleMembers";
import { useSafeCircleStore } from "@/stores/safeCircleStore";
import { useSafeCircleProfiles } from "@/hooks/useSafeCircleProfiles";
import { recipientsForCircle } from "@/utils/safecircle/notifications";
import { sendSosSms } from "@/utils/safecircle/sendSosSms";
import type { ContactStatus } from "@/hooks/useEmergencyContacts";
import { shouldProceedWithConfirm } from "@/utils/safecircle/sosCountdownGuard";
import {
  describeError,
  recordMicPermissionRequested,
  recordMicPermissionResult,
  writeSosEvent,
  type SosBreadcrumb,
} from "@/utils/safecircle/sosEventWrite";
import {
  recordArmNoUser,
  recordArmNoEventId,
  recordSmsNoContacts,
  recordSmsDeliveryFailed,
} from "@/utils/safecircle/sosInstrumentation";
import {
  getCurrentPositionSafe,
  watchPositionSafe,
  type WatchHandle,
} from "@/utils/geolocation";
import type {
  ProtectedProfile,
  SOSContactStatus,
  SOSEvent,
  SOSLocationSample,
  SOSStatus,
  SOSTriggerMethod,
} from "@/types/safecircle";

const COUNTDOWN_MS = 10_000;
const TRAIL_INTERVAL_MS = 5_000;
const AUDIO_DURATION_MS = 60_000;

// Sentry adapter for the pure SOS helpers — keeps the helpers
// Firebase/Sentry-decoupled and DI-testable.
const sentrySink = {
  addBreadcrumb: (b: SosBreadcrumb) => Sentry.addBreadcrumb(b),
  captureMessage: (
    message: string,
    ctx: {
      level?: "info" | "warning" | "error";
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
    },
  ) => Sentry.captureMessage(message, ctx),
  captureException: (
    err: unknown,
    ctx: { tags: Record<string, string>; extra: Record<string, unknown> },
  ) => Sentry.captureException(err, ctx),
};

export interface ArmOpts {
  triggerMethod: SOSTriggerMethod;
  profile: ProtectedProfile;
}

/**
 * Outcome of the emergency-contact SMS fan-out, surfaced to the UI so the
 * active screen never claims "help is on the way" when nothing was sent.
 *   - "idle":        not yet attempted (countdown / pre-confirm)
 *   - "sending":     send in flight
 *   - "sent":        at least one contact texted successfully
 *   - "failed":      attempted but every contact failed (Twilio/network)
 *   - "no-contacts": user has no emergency contacts saved
 *   - "no-location": confirmed without a valid GPS fix — SMS skipped so we
 *                    never text bogus 0,0 (null-island) coordinates
 */
export type SosSmsStatus =
  | "idle"
  | "sending"
  | "sent"
  | "failed"
  | "no-contacts"
  | "no-location";

export interface SOSEventState {
  event: SOSEvent | null;
  countdownRemainingMs: number;
  status: SOSStatus | "idle";
  responses: Record<string, SOSContactStatus>;
  /** Present when SOS is armed or active, including during countdown. */
  active: boolean;
  /** True only after the countdown expired and the event is confirmed. */
  confirmed: boolean;
  /** Emergency-contact SMS delivery state (#2/#3 — drives the active UI). */
  smsStatus: SosSmsStatus;
}

export interface UseSOSEventResult extends SOSEventState {
  arm: (opts: ArmOpts) => Promise<void>;
  /** Skip the countdown and confirm immediately. */
  confirmNow: () => Promise<void>;
  cancel: (pin?: string) => Promise<{ ok: boolean; reason?: string }>;
  resolve: () => Promise<void>;
}

/**
 * Central SOS state machine. Handles countdown → confirmed → resolved
 * transitions, writes location trail every 5s, captures a 60s audio clip,
 * and fans out notifications to the circle.
 */
export function useSOSEvent(): UseSOSEventResult {
  const { user } = useAuth();
  const activeProfileId = useSafeCircleStore((s) => s.activeProfileId);
  const { profiles } = useSafeCircleProfiles();
  const { members } = useCircleMembers(activeProfileId);

  const [event, setEvent] = useState<SOSEvent | null>(null);
  // #228: latest-value mirror of `event`. The countdown setInterval (armed with
  // [user]-only deps) captures confirmNowInternal at arm-time, whose closed-over
  // `event` is frozen at the arm-time snapshot (0,0 placeholder on the first
  // SOS). The fresh GPS fix lands in `event` STATE via the RTDB echo, but the
  // captured closure never sees it — so the timeout confirm texted stale/0,0
  // coords ("Send now", rebuilt every render, was fine). Reading coords through
  // this ref makes the timeout path see the same live value "Send now" does.
  const eventRef = useRef<SOSEvent | null>(null);
  eventRef.current = event;
  const [responses, setResponses] = useState<Record<string, SOSContactStatus>>(
    {},
  );
  const [countdownRemainingMs, setCountdownRemainingMs] = useState(0);
  const [smsStatus, setSmsStatus] = useState<SosSmsStatus>("idle");

  const watchHandleRef = useRef<WatchHandle | null>(null);
  const trailIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const activeProfileRef = useRef<ProtectedProfile | null>(null);
  // CRITICAL safety latch (#1 web QA): set true the instant Cancel is
  // pressed during the countdown, BEFORE any await. confirmNowInternal
  // checks it synchronously and bails, so a cancelled countdown can never
  // reach the confirm/SMS path even if the countdown timer's expiry
  // callback was already queued in the same tick as the cancel click.
  // (clearInterval alone cannot un-queue an already-fired interval
  // callback — this latch closes that race.) Keyed per armed event.
  const abortedRef = useRef(false);

  const status: SOSStatus | "idle" = event?.status ?? "idle";
  const active = status === "countdown" || status === "active";
  const confirmed = status === "active" || status === "responded";

  // Subscribe to the live event + its responses subnode.
  useEffect(() => {
    if (!user || !event?.eventId || !event?.profileId) return;
    const db = getDatabase();
    const eventRef = ref(
      db,
      `safecircle/${user.uid}/sosEvents/${event.profileId}/${event.eventId}`,
    );
    const unsubEvent = onValue(eventRef, (snap) => {
      const val = snap.val() as SOSEvent | null;
      // #1 (web QA): never let a cancelled/resolved RTDB echo resurrect the
      // event into the UI after a local cancel cleared it — that would
      // re-render the SOS screen for a session the user just aborted.
      // "auto_resolved" (server-side reaper) is terminal for the same reason.
      if (
        val &&
        val.status !== "cancelled" &&
        val.status !== "resolved" &&
        val.status !== "auto_resolved"
      ) {
        setEvent({ ...val, eventId: event.eventId });
      }
    });
    const respRef = ref(
      db,
      `safecircle/${user.uid}/sosEvents/${event.profileId}/${event.eventId}/responses`,
    );
    const unsubResp = onValue(respRef, (snap) => {
      const val = snap.val() as Record<string, SOSContactStatus> | null;
      setResponses(val ?? {});
    });
    return () => {
      unsubEvent();
      unsubResp();
    };
  }, [user, event?.eventId, event?.profileId]);

  const stopAll = useCallback(() => {
    if (watchHandleRef.current !== null) {
      watchHandleRef.current.clear();
      watchHandleRef.current = null;
    }
    if (trailIntervalRef.current) {
      clearInterval(trailIntervalRef.current);
      trailIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      /* already stopped */
    }
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = null;
    mediaRecorderRef.current = null;
  }, []);

  useEffect(() => () => stopAll(), [stopAll]);

  const startTrail = useCallback(
    (eventId: string, profileId: string) => {
      if (!user) return;
      const db = getDatabase();
      let latest: { lat: number; lng: number } | null =
        event && event.lat && event.lng
          ? { lat: event.lat, lng: event.lng }
          : null;
      watchHandleRef.current = watchPositionSafe(
        (pos) => {
          latest = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        },
        () => {
          /* swallow — trail writer falls back to last-known fix. */
        },
        { enableHighAccuracy: true, timeout: 10_000 },
      );
      trailIntervalRef.current = setInterval(() => {
        if (!latest) return;
        const sample: SOSLocationSample = {
          lat: latest.lat,
          lng: latest.lng,
          t: Date.now(),
        };
        const trailRef = push(
          ref(
            db,
            `safecircle/${user.uid}/sosEvents/${profileId}/${eventId}/locationHistory`,
          ),
        );
        void set(trailRef, sample);
        void update(
          ref(db, `safecircle/${user.uid}/sosEvents/${profileId}/${eventId}`),
          { lat: latest.lat, lng: latest.lng, updatedAt: sample.t },
        );
      }, TRAIL_INTERVAL_MS);
    },
    [user, event],
  );

  const startAudioCapture = useCallback(
    async (eventId: string, profileId: string) => {
      if (!user) return;
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        return;
      }
      recordMicPermissionRequested(sentrySink);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        recordMicPermissionResult(sentrySink, true);
        audioStreamRef.current = stream;
        const recorder = new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;
        audioChunksRef.current = [];
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
        };
        recorder.onstop = async () => {
          const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          if (blob.size === 0) return;
          try {
            const storage = getStorage();
            const path = `sos/${user.uid}/${eventId}.webm`;
            const sRef = storageRef(storage, path);
            await uploadBytes(sRef, blob, { contentType: "audio/webm" });
            const db = getDatabase();
            await update(
              ref(
                db,
                `safecircle/${user.uid}/sosEvents/${profileId}/${eventId}`,
              ),
              { audioRecordingRef: path },
            );
          } catch (err) {
            console.error("[useSOSEvent] audio upload failed", err);
          }
        };
        recorder.start();
        setTimeout(() => {
          try {
            recorder.stop();
          } catch {
            /* already stopped */
          }
          stream.getTracks().forEach((t) => t.stop());
        }, AUDIO_DURATION_MS);
      } catch (err) {
        recordMicPermissionResult(sentrySink, false, err);
        // Denial is expected (user can decline mic) — breadcrumb only, no
        // captureException. Audio is best-effort; SOS continues without it.
        const info = describeError(err);
        console.warn(
          "[useSOSEvent] mic permission denied",
          info.code,
          info.message,
        );
      }
    },
    [user],
  );

  const fanOutNotifications = useCallback(
    async (ev: SOSEvent, profile: ProtectedProfile) => {
      if (!user) return;
      const db = getDatabase();
      const recipients = recipientsForCircle(members, "SOS_TRIGGERED", profile);
      const notifiedContacts: string[] = [];
      const responsesInit: Record<string, SOSContactStatus> = {};
      const now = Date.now();
      for (const m of recipients) {
        if (!m.linkedUid) continue;
        notifiedContacts.push(m.linkedUid);
        responsesInit[m.linkedUid] = {
          uid: m.linkedUid,
          notifiedAt: now,
        };
        const notifRef = push(
          ref(db, `safeCircleNotifications/${m.linkedUid}`),
        );
        await set(notifRef, {
          id: notifRef.key,
          type: "SOS_TRIGGERED",
          toUid: m.linkedUid,
          fromUid: user.uid,
          fromName: user.displayName || user.email || "Someone",
          profileId: profile.profileId,
          title: `🚨 SOS from ${profile.name}`,
          body: `Emergency alert — location updating live.`,
          data: {
            type: "SOS_TRIGGERED",
            sosEventId: ev.eventId,
            profileId: profile.profileId,
            walkerUid: user.uid,
          },
          createdAt: now,
        });
      }
      await update(
        ref(db, `safecircle/${user.uid}/sosEvents/${profile.profileId}/${ev.eventId}`),
        {
          notifiedContacts,
          responses: responsesInit,
        },
      );
    },
    [user, members],
  );

  const arm = useCallback(
    async ({ triggerMethod, profile }: ArmOpts) => {
      if (!user) {
        // No authed user at arm time — the SOS silently does nothing. Capture
        // it: a signed-in user reaching SOS should always have a user here, so
        // this is an unexpected state, not a benign guard.
        recordArmNoUser(sentrySink, triggerMethod);
        return;
      }
      abortedRef.current = false; // fresh countdown — clear any prior abort
      activeProfileRef.current = profile;
      const db = getDatabase();
      const eventRef = push(
        ref(db, `safecircle/${user.uid}/sosEvents/${profile.profileId}`),
      );
      const eventId = eventRef.key;
      if (!eventId) {
        // push().key was null — the SOS event can't be created, so the whole
        // arm silently aborts (no countdown, no active screen). Capture it.
        recordArmNoEventId(sentrySink, {
          triggerMethod,
          uid: user.uid,
          profileId: profile.profileId,
        });
        return;
      }
      const now = Date.now();
      const lat = 0;
      const lng = 0;
      const initial: SOSEvent = {
        eventId,
        profileId: profile.profileId,
        mode: profile.mode,
        triggeredAt: now,
        triggerMethod,
        lat,
        lng,
        status: "countdown",
        audioUrl: "",
        notifiedContacts: [],
        escalationLevel: 1,
      };
      // Wrap the RTDB SOS event write: a permission-denied rejection
      // (open ticket: JAVASCRIPT-REACT-4) must not propagate up — the user
      // can still see the Emergency Help panel, call 911, and share location
      // even when the write fails. Set local state optimistically so the
      // countdown / fallback UI still shows.
      const writeResult = await writeSosEvent(
        {
          setEvent: () => set(eventRef, initial),
          addBreadcrumb: sentrySink.addBreadcrumb,
          captureException: sentrySink.captureException,
        },
        { uid: user.uid, profileId: profile.profileId, eventId },
      );
      if (!writeResult.ok && writeResult.error) {
        // code/message/stack — NOT JSON.stringify(error), which renders
        // Firebase errors as `{}`.
        console.error(
          "[useSOSEvent] RTDB SOS write failed:",
          writeResult.error.code,
          writeResult.error.message,
          writeResult.error.stack,
        );
      }
      setEvent(initial);
      setSmsStatus("idle"); // reset per-event so a prior outcome can't leak
      // Best-effort immediate position grab — otherwise trail writer fills in.
      // Wrap so a permission throw or native bridge failure on iOS can't
      // escape into the SOS path.
      void getCurrentPositionSafe({ timeout: 5_000 })
        .then((pos) => {
          Sentry.addBreadcrumb({
            category: "sos",
            level: "info",
            message: "sos.location_acquired",
            data: { lat: pos.lat, lng: pos.lng },
          });
          return update(eventRef, { lat: pos.lat, lng: pos.lng }).catch(
            (err) => {
              Sentry.captureException(err, {
                tags: {
                  area: "sos",
                  feature: "sos",
                  phase: "rtdb_position_update",
                },
              });
            },
          );
        })
        .catch((err) => {
          Sentry.addBreadcrumb({
            category: "sos",
            level: "warning",
            message: "getCurrentPosition error",
            data: { message: (err as Error)?.message ?? String(err) },
          });
        });
      // #1 (web QA): compute the deadline from a FRESH timestamp here, NOT
      // from `now` captured at the top of arm(). The awaits above
      // (writeSosEvent + the position IIFE) can consume real time on a slow
      // connection; using the stale `now` made the deadline already-elapsed
      // by the time the interval started, firing confirm almost instantly
      // while the UI still showed the full countdown. The full window must
      // start counting from when the countdown actually begins.
      setCountdownRemainingMs(COUNTDOWN_MS);
      const deadline = Date.now() + COUNTDOWN_MS;
      countdownIntervalRef.current = setInterval(() => {
        const remaining = Math.max(0, deadline - Date.now());
        setCountdownRemainingMs(remaining);
        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current!);
          countdownIntervalRef.current = null;
          void confirmNowInternal(profile, eventId);
        }
      }, 200);
    },
    // confirmNowInternal is stable via ref; eslint disabled for deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  const confirmNowInternal = useCallback(
    async (profile: ProtectedProfile, eventId: string) => {
      // #1 (web QA, CRITICAL): if the user cancelled during the countdown,
      // never proceed to confirm — even if this was invoked by a timer
      // callback that had already been queued before cancel ran. This is
      // the load-bearing guard that makes "Cancel SOS" reliable. Decision
      // lives in a pure, unit-tested helper.
      if (
        !shouldProceedWithConfirm({
          aborted: abortedRef.current,
          hasUser: !!user,
        })
      ) {
        if (abortedRef.current) {
          Sentry.addBreadcrumb({
            category: "sos",
            level: "info",
            message: "sos.confirm_suppressed_after_cancel",
            data: { eventId },
          });
        }
        return;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setCountdownRemainingMs(0);
      const db = getDatabase();
      const eventPath = `safecircle/${user.uid}/sosEvents/${profile.profileId}/${eventId}`;
      // The confirm path (countdown expiry calls this via `void`, and the
      // "Send now" button awaits it from an onClick) reaches RTDB
      // `update` + the fan-out write loop. A permission-denied rejection
      // here was previously an unhandled rejection — the single most
      // likely WKWebView-crash source. Swallow + capture with full
      // code/message/stack; the active SOS UI (panel, tel:911, trail,
      // audio best-effort) must keep working regardless.
      try {
        await update(ref(db, eventPath), { status: "active" });
        // Fan-out notifications, start trail, start audio.
        // #228: read the event through eventRef.current (latest value) instead
        // of the closed-over `event`, so the countdown-expiry path uses the live
        // GPS fix rather than the arm-time 0,0 placeholder. The RTDB echo may
        // have raced the confirm — prefer the ref, but never below the confirm's
        // own snapshot. `event` (closure) is kept only as a last-resort fallback.
        const latest = eventRef.current;
        const lat = latest?.lat ?? event?.lat ?? 0;
        const lng = latest?.lng ?? event?.lng ?? 0;
        const currentEvent: SOSEvent = {
          eventId,
          profileId: profile.profileId,
          mode: profile.mode,
          triggeredAt: Date.now(),
          triggerMethod: latest?.triggerMethod ?? event?.triggerMethod ?? "button",
          lat,
          lng,
          status: "active",
          audioUrl: "",
          notifiedContacts: [],
          escalationLevel: 1,
        };
        // #228: record WHICH location source the confirm used and whether it
        // was a usable fix — so we can verify in Sentry that the fix took.
        // Privacy: NO coordinates in the payload — only a source label + a
        // boolean (keeps #225's no-coords-in-Sentry invariant).
        Sentry.addBreadcrumb({
          category: "sos",
          level: "info",
          message: "sos.confirm_location_source",
          data: {
            source: latest ? "live-ref" : event ? "closure-fallback" : "none",
            hasFix: !(lat === 0 && lng === 0),
            eventId,
          },
        });
        await fanOutNotifications(currentEvent, profile);
        // SMS the user's emergency contacts (the thing the UI/AI promised but
        // the SOS path never did). Best-effort + isolated: a Twilio/network
        // failure must NOT break the active SOS state (panel, 911, trail still
        // work). Runs alongside — not instead of — the in-app circle notify.
        //
        // #2/#3 (web QA): the outcome is now surfaced via smsStatus so the
        // active screen never claims "help is on the way" when nothing was
        // sent, AND we refuse to text a bogus 0,0 (null-island) location.
        setSmsStatus("sending");
        void (async () => {
          try {
            console.log("[SOS-SMS] confirm reached SMS block; reading contacts");
            // #3: a valid fix means finite coords that are not the 0,0
            // null-island default. Without one, texting a maps link to
            // "0,0" is worse than useless — skip the SMS and flag it.
            const hasValidFix =
              Number.isFinite(currentEvent.lat) &&
              Number.isFinite(currentEvent.lng) &&
              !(currentEvent.lat === 0 && currentEvent.lng === 0);
            if (!hasValidFix) {
              console.warn(
                "[SOS-SMS] no valid location fix — skipping SMS to avoid 0,0",
              );
              Sentry.addBreadcrumb({
                category: "sos",
                level: "warning",
                message: "sos.emergency_sms_skipped_no_location",
              });
              setSmsStatus("no-location");
              return;
            }
            const snap = await get(
              ref(db, `users/${user.uid}/emergencyContacts`),
            );
            const raw = snap.val();
            // Two-step opt-in: carry `status` through so sendSosSms's
            // validSosPhones gates to ELIGIBLE contacts (confirmed OR legacy/
            // grandfathered — pending/declined excluded). Missing status =
            // legacy = eligible (fail-safe: never drop a pre-two-step contact).
            const contacts = raw && typeof raw === "object"
              ? Object.values(
                  raw as Record<
                    string,
                    { name?: string; phone?: string; status?: ContactStatus }
                  >,
                )
                  .filter((c) => c && typeof c.phone === "string")
                  .map((c) => ({
                    name: c.name ?? "",
                    phone: c.phone as string,
                    status: c.status,
                  }))
              : [];
            console.log(
              `[SOS-SMS] emergencyContacts loaded: count=${contacts.length}`,
              contacts.map((c) => c.phone),
            );
            if (contacts.length === 0) {
              console.warn("[SOS-SMS] no emergency contacts — nothing to text");
              // A SOS that reaches confirm but has no contacts to text sends no
              // SMS at all. Capture it: it may be legitimate (user has none) or
              // a data-load failure, and today it's console-only.
              recordSmsNoContacts(sentrySink, eventId);
              setSmsStatus("no-contacts");
              return;
            }
            const outcome = await sendSosSms({
              contacts,
              lat: currentEvent.lat,
              lng: currentEvent.lng,
              eventShortCode: eventId.slice(-6),
              profile,
              // A2P 10DLC: the emergency-contact SMS body must match the
              // registered campaign sample verbatim (no medical appendix), and
              // stay single-segment. Medical detail still ships on the in-app
              // "Share with 911" draft (SOSActiveScreen/SOSButton).
              includeMedical: false,
              getIdToken: () => user.getIdToken(),
            });
            console.log(
              `[SOS-SMS] sendSosSms outcome: ok=${outcome.ok} sent=${outcome.sent} failed=${outcome.failed} detail=${outcome.errorDetail ?? "none"}`,
              outcome.results,
            );
            // #2: reflect the REAL outcome. "sent" only if ≥1 contact
            // actually received the text; otherwise "failed" so the UI can
            // tell the user and offer the native-SMS fallback.
            setSmsStatus(outcome.ok && outcome.sent > 0 ? "sent" : "failed");
            Sentry.addBreadcrumb({
              category: "sos",
              level: outcome.ok ? "info" : "warning",
              message: "sos.emergency_sms",
              data: { sent: outcome.sent, failed: outcome.failed },
            });
            // A non-ok outcome (HTTP non-2xx, network error, or eligibility
            // filtering every contact out — errorDetail "no_emergency_contacts")
            // means the emergency SMS did not reach at least one contact, yet
            // sendSosSms never throws, so this path was breadcrumb-only. Capture
            // it so a real Twilio/transport failure creates a Sentry issue.
            // errorDetail carries a server code/message only — never phone
            // numbers or coordinates; counts go in structured context, not the
            // message body.
            if (!outcome.ok) {
              recordSmsDeliveryFailed(
                sentrySink,
                {
                  sent: outcome.sent,
                  failed: outcome.failed,
                  errorDetail: outcome.errorDetail,
                },
                { eventId, recipientCount: contacts.length },
              );
            }
          } catch (smsErr) {
            console.error("[SOS-SMS] SMS block threw:", smsErr);
            setSmsStatus("failed");
            Sentry.captureException(smsErr, {
              tags: { area: "sos", feature: "sos", phase: "emergency_sms" },
            });
          }
        })();
        startTrail(eventId, profile.profileId);
        void startAudioCapture(eventId, profile.profileId);
      } catch (err) {
        const info = describeError(err);
        Sentry.addBreadcrumb({
          category: "sos",
          level: "error",
          message: "sos.rtdb_write_failed",
          data: { phase: "confirm", code: info.code },
        });
        Sentry.captureException(err, {
          tags: { area: "sos", feature: "sos", phase: "confirm" },
          extra: {
            code: info.code,
            message: info.message,
            stack: info.stack,
            eventId,
          },
        });
        console.error(
          "[useSOSEvent] SOS confirm failed:",
          info.code,
          info.message,
          info.stack,
        );
      }
    },
    [user, event, fanOutNotifications, startTrail, startAudioCapture],
  );

  const confirmNow = useCallback(async () => {
    const profile = activeProfileRef.current;
    if (!profile || !event?.eventId) return;
    await confirmNowInternal(profile, event.eventId);
  }, [event, confirmNowInternal]);

  const cancel = useCallback(
    async (pin?: string) => {
      if (!user || !event?.eventId || !event?.profileId) {
        return { ok: false, reason: "no-event" };
      }
      const profile =
        activeProfileRef.current ??
        profiles.find((p) => p.profileId === event.profileId) ??
        null;
      // During countdown anyone can cancel without PIN. After confirmation
      // the profile's stealthConfig.pin is required (if configured).
      if (event.status !== "countdown") {
        const requiredPin = profile?.stealthConfig?.pin;
        if (requiredPin && pin !== requiredPin) {
          return { ok: false, reason: "invalid-pin" };
        }
      } else {
        // #1 (web QA, CRITICAL): latch abort SYNCHRONOUSLY, before any
        // await, the moment a countdown cancel is accepted. This guarantees
        // a concurrently-queued countdown-expiry callback sees the abort
        // and suppresses confirm — clearing the interval below is not
        // enough on its own because an already-fired callback can't be
        // un-queued. Only set for countdown cancels (a failed PIN cancel of
        // an active SOS must NOT abort an in-flight emergency).
        abortedRef.current = true;
      }
      stopAll();
      const db = getDatabase();
      await update(
        ref(
          db,
          `safecircle/${user.uid}/sosEvents/${event.profileId}/${event.eventId}`,
        ),
        {
          status: "cancelled",
          cancelledAt: Date.now(),
        },
      );
      setEvent(null);
      setResponses({});
      setCountdownRemainingMs(0);
      return { ok: true };
    },
    [user, event, profiles, stopAll],
  );

  const resolve = useCallback(async () => {
    if (!user || !event?.eventId || !event?.profileId) return;
    stopAll();
    const db = getDatabase();
    const now = Date.now();
    await update(
      ref(
        db,
        `safecircle/${user.uid}/sosEvents/${event.profileId}/${event.eventId}`,
      ),
      {
        status: "resolved",
        resolvedAt: now,
        resolvedBy: user.uid,
      },
    );
    // Notify the circle that it's resolved.
    const profile = activeProfileRef.current;
    if (profile) {
      const recipients = recipientsForCircle(members, "SOS_RESOLVED", profile);
      for (const m of recipients) {
        if (!m.linkedUid) continue;
        const notifRef = push(
          ref(db, `safeCircleNotifications/${m.linkedUid}`),
        );
        await set(notifRef, {
          id: notifRef.key,
          type: "SOS_RESOLVED",
          toUid: m.linkedUid,
          fromUid: user.uid,
          fromName: user.displayName || user.email || "Someone",
          profileId: profile.profileId,
          title: `${profile.name} marked SOS resolved`,
          body: "They're safe. Thanks for responding.",
          data: {
            type: "SOS_RESOLVED",
            sosEventId: event.eventId,
            profileId: profile.profileId,
          },
          createdAt: now,
        });
      }
    }
    setEvent(null);
    setResponses({});
  }, [user, event, members, stopAll]);

  // NOTE: the responder-side ack write lives in SOSReceiverScreen.handleRespond,
  // which correctly targets the WALKER-owned path
  // `safecircle/{walkerUid}/sosEvents/.../responses/{responderUid}` — the same
  // path the walker reader above subscribes to. The responder is authorised
  // because fanOutNotifications (below) seeds their leaf {uid, notifiedAt} at
  // trigger time; the RTDB rule lets them merge their ack into that existing
  // leaf. A former `respond()` helper here wrote to `safecircle/{user.uid}/...`
  // (the responder's OWN subtree), where the walker has no read access, so
  // acks landed invisibly. It was also unused (no consumer destructured it).
  // Removed; see buildResponderAckPath in utils/safecircle/sosResponse.ts.

  return {
    event,
    countdownRemainingMs,
    status,
    responses,
    active,
    confirmed,
    smsStatus,
    arm,
    confirmNow,
    cancel,
    resolve,
  };
}
